import WKT from 'ol/format/WKT';
import Feature from 'ol/Feature';
import {useCodeListStore} from '@/app/shared/state/CodeListModule';
import KlUploadZone from '@/app/shared/components/kl-upload/kl-upload-zone/kl-upload-zone.vue';
import {first, isEmpty, filter, every, forEach, find, isEqual, cloneDeep, size} from 'lodash-es';
import KlDrawZoneMapUtils, {
    EDrawZoneMapFeaturePropertyKey
} from '../../kl-draw-zone-map-utils';
import {useUserStore} from "@/app/shared/state/UserDataModule";
import {AnalyticsService, EAnalyticsCategory, EAnalyticsDrawZoneActions} from '@/app/shared/service/analytics-service';
import {computed, defineComponent, inject, nextTick, reactive, ref, watch} from 'vue';
import {
    IDrawZoneMapGeometry,
    IDrawZoneOrgInfo,
    IDrawZoneGeometryInfo,
} from '../../kl-draw-zone-map';
import KlSidebar from '@/app/shared/components/kl-draw-zone-map/components/kl-sidebar.vue';
import KlDrawZoneSidebarFiller from '../kl-draw-zone-sidebar-filler.vue';
import KlDrawZoneSidebarHeader from '../kl-draw-zone-sidebar-header.vue';
import {useRouter} from '@/router/router-vue3';
import KlDrawZoneSidebarDownload from '../kl-draw-zone-sidebar-download.vue';
import KlDrawZoneSidebarGeometryInfo from '../kl-draw-zone-sidebar-geometry-info.vue';
import {IDrawZoneContext} from '@/app/shared/components/kl-draw-zone-map/kl-draw-zone-context';
import KlDrawZoneSidebarImport from '../kl-draw-zone-sidebar-import.vue';
import KlDrawZoneSidebarKlbList from '../kl-draw-zone-sidebar-klb-list.vue';
import KlDrawZoneSidebarGeometryCreateEdit from '../kl-draw-zone-sidebar-geometry-create-edit.vue';
import KlDrawZoneSidebarGeometryActions from '../kl-draw-zone-sidebar-geometry-actions.vue';
import {useGeometryProjectionStore} from '../../geometry-projection-store';
import KlDrawZoneSidebarImportLambertWarning from '../kl-draw-zone-sidebar-import-lambert-warning.vue';
import KlDrawZoneSidebarAreaSection from '../kl-draw-zone-sidebar-area-section.vue';
import {klValidateYup} from '@/app/shared/components/kl-form-fields/kl-field-validation-util';
import * as yup from 'yup';
import {useFormatting} from '@/composables/formatting';
import {useDrawZoneImportContext} from '@/app/shared/components/kl-draw-zone-map/kl-draw-zone-import-context';

export interface IDrawZone {
    id: number; // used to link DrawZone to ol.Feature
    name?: string;

    isValid?: boolean; // undefined = not validated yet
    errorMessages?: string[];

    price?: number; // = de prijs als dit gebruikt wordt voor een maprequest
    area: number; // = de oppervlakte
    length: number; // = de omtrek
    coordinates: number[][][] | number[][][][]; // array of polygon[] | multipolygon[]; polygon = array of coordinates
    crs: {
        type: string,
        properties: {
            name: string,
        },
    };
    type: string;

    orgZoneInfo?: IDrawZoneOrgInfo;
    geometryInfo?: IDrawZoneGeometryInfo;
}

export enum EDrawZoneMapState {
    overview,
    create,
    edit,
    import
}

export default defineComponent({
    name: 'KlDrawZoneSidebar',
    components: {
        KlDrawZoneSidebarImportLambertWarning,
        KlDrawZoneSidebarGeometryCreateEdit,
        KlDrawZoneSidebarGeometryInfo,
        KlDrawZoneSidebarGeometryActions,
        KlDrawZoneSidebarKlbList,
        KlDrawZoneSidebarDownload,
        KlUploadZone,
        KlSidebar,
        KlDrawZoneSidebarFiller,
        KlDrawZoneSidebarHeader,
        KlDrawZoneSidebarImport,
        KlDrawZoneSidebarAreaSection,
    },
    emits: ['zoom-to-features', 'zoom-to-feature', 'loading', 'state', 'input', 'zone-price', 'remove-geometry', 'toggle-show-active'],
    props: {
        modMaprequestZone: { type: Boolean, default: false },
        modDisabled: { type: Boolean, default: false },
        reference: { type: String, default: '', },
        rules: { type: Object as () => yup.Schema, default: undefined, },
        includeImkl: { type: Boolean, default: false, },
        forMaprequest: { type: Boolean, default: false, },
        noConfirmation: { type: Boolean, default: false, },
        modEnableMultiZones: { type: Boolean, default: false, },

        forKlbZones: { type: Boolean, default: false, },
        canAddKlbZone: { type: Boolean, default: false, },
        canAddKlimZone: { type: Boolean, default: false, },
        addKlbZoneLink: { type: Object as () => any, default: null, },
        addKlimZoneLink: { type: Object as () => any, default: null, },
        showKlbArchivedZoneToggle: { type: Boolean, default: false, },
        klbArchivedZoneToggleValue: { type: Boolean, default: false, },

        allowMultiPolygons: { type: Boolean, default: false },
        forGeometry: { type: Boolean, default: false },
        showRemoveGeometry: { type: Boolean, default: false },

        projection: { type: String, required: true },
        disableReprojectionWarning: { type: Boolean, default: false },

        simpleDrawMode: { type: Boolean,  default: false },
    },
    setup(props, {emit}) {

        const _importHelpLink: string = import.meta.env.VITE_LINKS_IMPORT_HELP;

        const _context = inject<IDrawZoneContext>('kl-draw-zone-map-context');
        const _importContext = useDrawZoneImportContext({
                disableReprojectionWarning: props.disableReprojectionWarning,
                allowMultiPolygons: props.allowMultiPolygons,
                modEnableMultiZones: props.modEnableMultiZones,
                projection: props.projection,

                getZones: () => zones.value,
                createDrawZone: (feature: Feature) => _createDrawZone(feature),
                validateZone: async (zone: IDrawZone): Promise<boolean> => _validateZone(zone),

                handleShowPreview: (features: Feature[]) => {
                    forEach(features, (feature: Feature) => {
                        _context.addFeature(feature);
                    });
                    emit('zoom-to-features', features);
                },
                handleRemovePreview: (features: Feature[]) => {
                    forEach(features, (feature: Feature) => {
                        _context.removeFeature(feature);
                    });
                },
                handleCancelImport: () => {
                    if (props.simpleDrawMode) {
                        _setState(EDrawZoneMapState.create);
                    }
                    else {
                        _setState(EDrawZoneMapState.overview);
                    }
                },
                handleSingleImportDone: (newFeature: Feature, newZone: IDrawZone) => {
                    _addDraftOpt([newFeature], [newZone]);
                    _editAfterImport(newZone.id);
                },
                handleMultiImportDone: (newFeatures: Feature[], newZones: IDrawZone[]) => {
                    console.assert(!props.simpleDrawMode, 'handleMultiImportDone not supported for simpleDrawMode');
                    _addDraftOpt(newFeatures, newZones);
                    showList();
                    emit('zoom-to-features', newFeatures);
                },
            });

        const router = useRouter();
        const {formatCurrency} = useFormatting();

        const zones = ref<IDrawZone[]>(null);
        const _zonesBackup = ref<IDrawZone[]>(null);

        const currentZone = ref<IDrawZone>(null);
        const currentZoneError = ref<string>(null); // fix voor vue update trigger (anders ziet vue niet dat de zones[].error is aangepast)

        // workflows
        // TO BE REFACTORED: [editZone, createZone, importZone] enkel aanpassen via _setState
        // snelle poging om hier computed props van te maken is gefaald (UI reageert niet correct)
        const _state = ref<EDrawZoneMapState>(EDrawZoneMapState.overview);
        const listZones = ref(false);
        const editZone = ref(false);
        const createZone = ref(false);
        const importZone = ref(false);
        const addingNewZone = ref(false); // = during createZone step 1 & 2 (editZone)

        const editZoneName = ref(false);
        const currentZoneName = ref<string>(null);
        const editZoneNameHasValidationError = ref(false);
        const _manualZoneNames = ref<{ [id: number]: string }>({});
        const _manualZoneNamesBackup = ref<{ [id: number]: string }>({});

        const mapRequestLargePrice = computed(() => useCodeListStore().mapRequestPrices?.mapRequestLarge);
        const mapRequestSmallPrice = computed(() => useCodeListStore().mapRequestPrices?.mapRequestSmall);
        const imklPrice = computed(() => useCodeListStore().mapRequestPrices?.imklPrice);
        const mapRequestSizeTreshold = computed(() => useCodeListStore().mapRequestPrices?.areaThreshold);


        const canEditZoneName = computed((): boolean => {
            return props.modEnableMultiZones;
        });

        const importMoreInfoLink = computed((): string => {
            if (!props.forMaprequest) {
                return null;
            }
            return _importHelpLink;
        });

        const maxFilesToImport = computed((): number => {
            return props.modEnableMultiZones ? 10000 : 1;
        });

        const currentPolygonFeatureAreaFormatted = computed((): string | undefined => {
            // empty when 'area = 0'
            if (_context.currentAreaRef.value) {
                return KlDrawZoneMapUtils.formatArea(_context.currentAreaRef.value);
            }
            return undefined;
        });

        const currentZoneDetailInfo = computed((): string | undefined => {
            // empty when 'area = 0'
            if (_context.currentAreaRef.value) {
                return _getZoneDetailInfo(_context.currentAreaRef.value);
            }
            return undefined;
        });

        const currentZonePrice = computed((): number | undefined => {
            // empty when 'area = 0'
            if (_context.currentAreaRef.value) {
                return _getZonePrice(_context.currentAreaRef.value);
            }
            return undefined;
        });

        const zoneDetailTitle = computed(() => {
            return props.modMaprequestZone && currentZone.value?.name ? currentZone.value?.name : `Zone bewerken`;
        });

        const zoneDetailDescription = computed(() => {
            return props.modMaprequestZone ? `Bewerk uw planaanvraagzone op de kaart` : `Bewerk uw zone op de kaart`;
        });

        const applyVat = computed(() => {
            return useUserStore().applyVat;
        });

        const sidebarTitle = computed(() => {
            if (props.forKlbZones) {
                return 'Zones';
            }

            const suffix = props.modEnableMultiZones ? '(s)' : '';
            return props.modMaprequestZone ? `Uw planaanvraagzone${suffix}` : `Uw zone${suffix}`;
        });

        const sidebarText = computed(() => {
            return `Teken uw ${props.modMaprequestZone ? `planaanvraagzone` : `zone`} op de kaart.<br> Sluit de zone door te dubbelklikken.`;
        });

        const importButtonLabel = computed(() => {
            const suffix = props.modEnableMultiZones ? '(s)' : '';
            return `Importeer zone${suffix}`;
        });

        const canAddMoreZones = computed(() => {
            if (props.forKlbZones) {
                return false;
            }

            return props.modEnableMultiZones || isEmpty(zones.value);
        });

        const hasZones = computed(() => {
            return !isEmpty(zones.value);
        });

        const isConfirmVisible = computed(() => {
            return !props.noConfirmation;
        });

        const isRemoveVisible = computed(() => {
            return (!isConfirmVisible.value || !addingNewZone.value) && editZone.value;
        });

        watch(
            () => props.reference,
            (newRef: string) => {
                KlDrawZoneMapUtils.updateZoneNames(zones.value, newRef, _manualZoneNames.value);
            },
            {immediate: false, deep: true});

        const onCloseWarnings = () => {
            _importContext.clearImportErrors();
        }

        const _clearAll = () => {
            _importContext.clearImportErrors();

            zones.value?.forEach((cz: IDrawZone) => remove(cz));
            _setCurrentZone(null);
        }

        const onEditZoneName = () => {
            editZoneName.value = true;
            currentZoneName.value = currentZone.value.name;
            onInputZoneName(currentZoneName.value);
        }
        const onStopEditZoneName = () => {
            if (editZoneNameHasValidationError.value) {
                return;
            }
            if (currentZone.value.name !== currentZoneName.value) {
                currentZone.value.name = currentZoneName.value;
                _manualZoneNames.value[currentZone.value.id] = currentZoneName.value;
                KlDrawZoneMapUtils.updateZoneNames(zones.value, props.reference, _manualZoneNames.value);
            }
            currentZoneName.value = null;
            editZoneName.value = false;
        }
        const onInputZoneName = (newName: string) => {
            editZoneNameHasValidationError.value = size(newName) > 100;
        }

        const _getZonePrice = (area: number): number | undefined => {
            if (props.modMaprequestZone && area) {
                return Math.round(area) >= mapRequestSizeTreshold.value ? mapRequestLargePrice.value : mapRequestSmallPrice.value;
            }
            return undefined;
        }

        const getZoneListInfo = (cz: IDrawZone): string => {
            if (props.modMaprequestZone) {
                return cz.area && mapRequestSizeTreshold.value ? `Opp. ${Math.round(cz.area) >= mapRequestSizeTreshold.value ? `groter` : `kleiner`} dan ${mapRequestSizeTreshold.value}&nbsp;m²` : null;
            }

            return null;
        }

        const _getZoneDetailInfo = (area: number) => {
            return props.modMaprequestZone && area && mapRequestSizeTreshold.value
                ? `Planaanvraag van ${Math.round(area) >= mapRequestSizeTreshold.value ? `>` : `<`}${mapRequestSizeTreshold.value} m²`
                : null;
        }

        // probleem met reactiviteit indien dit een computed is
        const getTotalPrice = (): number => {
            if (!props.modMaprequestZone) {
                return 0;
            }
            let price: number = getTotalImklPrice();
            zones.value?.forEach((cz: IDrawZone) => price += cz.price);
            return price;
        }

        const getTotalImklPrice = (): number => {
            if (props.includeImkl && imklPrice.value && !isEmpty(zones.value)) {
                return imklPrice.value * zones.value.length;
            }
            return 0;
        }

        const getImklDescription = (): string => {
            if (!props.includeImkl || !imklPrice.value || isEmpty(zones.value)) {
                return null;
            }
            if (zones.value.length === 1) {
                return `+ 1 IMKL-pakket (€${imklPrice.value})`;
            }
            return `+ ${zones.value.length} IMKL-pakketten (€${imklPrice.value})`;
        }


        //////////////////////////////////////////////////////////////////////////////
        // TO REFACTOR: for now direct convert of ol events into methods
        const onDrawInteraction_DrawEnd = async (feature: Feature) => {
            await _addDraft(feature);
            _editCurrentZone();
        }
        const onSelectInteraction_Select = async (selected: Feature[], deselected: Feature[]) => {
            if (props.forKlbZones) {
                // in this scenario, the 'select-interaction' is only used to highlight the selected property on the map
                // the actual 'select' action is handle by a map.click event
                return;
            }

            if (!isEmpty(deselected)) {
                const valid = await confirm();

                // re-select when not valid
                if (!valid && currentZone.value) {
                    _reselectZone(currentZone.value.id); // let op dat dit de backup niet aanpast!
                    _editCurrentZone();
                    return;
                }
            }
            if (!isEmpty(selected)) {
                const currentFeature = first(selected);
                edit(currentFeature.get(EDrawZoneMapFeaturePropertyKey.drawZoneId));
            }
        }
        const onModifyInteraction_ModifyEnd = async (feature: Feature) => {
            await _updateZone();
        }

        // END TO REFACTOR
        //////////////////////////////////////////////////////////////////////////////

        const showList = () => {
            _setState(EDrawZoneMapState.overview);
            _setCurrentZone(null);

            _context.selectFeature(null);
        }

        const onCreate = () => {
            AnalyticsService.SendEvent(EAnalyticsCategory.drawZone, EAnalyticsDrawZoneActions.draw);

            _startAddingNewZones();

            _importContext.clearImportErrors();
            _setState(EDrawZoneMapState.create);
            _setCurrentZone(null);

            _notifyStateAsInvalidBecauseEditing();
        }

        const onZoneImport = () => {
            _importContext.clearImportErrors();
            _setState(EDrawZoneMapState.import);
        }

        const _addZones = (newZones: IDrawZone[]) => {
            if (!zones.value) {
                zones.value = [];
            }
            zones.value = zones.value.concat(newZones);
            KlDrawZoneMapUtils.updateZoneNames(zones.value, props.reference, _manualZoneNames.value);
        }

        // warning: side effects > feature is modified
        const _createDrawZone = (feature: Feature): IDrawZone => {
            KlDrawZoneMapUtils.removeDuplicateCoordinates(feature, props.allowMultiPolygons);
            const newZone = KlDrawZoneMapUtils.createDrawZone(feature);
            newZone.price = _getZonePrice(newZone.area); // TODO?? move to KlDrawZoneMapUtils.createDrawZone?

            // TODO: REFACTOR
            // TEMP: add cross-link
            feature.set(EDrawZoneMapFeaturePropertyKey.drawZoneId, newZone.id);

            return newZone;
        }

        const _addDraft = async (feature: Feature): Promise<IDrawZone> => {
            _context.addFeature(feature); // add feature to map before awaits (visual optimisation)

            const newZone = _createDrawZone(feature); // modifies feature
            await _validateZoneWithLoading(newZone); // modifies newZone

            _setCurrentZone(newZone);

            _addZones([newZone]); // modifies this.zones
            _emitResult();
            return newZone;
        }

        const _addDraftOpt = (newFeatures: Feature[], newZones: IDrawZone[]) => {
            forEach(newFeatures, (feature: Feature) => {
                _context.addFeature(feature);
            });

            _addZones(newZones); // modifies this.zones
            _emitResult();
        }

        const onConfirm = async (): Promise<boolean> => {
            const valid = await _updateZone();
            if (valid) {
                showList();
            }
            return valid;
        }

        const _updateZone = async (): Promise<boolean> => {

            if (editZoneName.value) {
                onStopEditZoneName();
            }

            const currentFeature: Feature = _context.findFeature(currentZone.value.id);
            KlDrawZoneMapUtils.removeDuplicateCoordinates(currentFeature, props.allowMultiPolygons);
            KlDrawZoneMapUtils.updateDrawZone(currentZone.value, currentFeature);
            KlDrawZoneMapUtils.updateZoneNames(zones.value, props.reference, _manualZoneNames.value);
            currentZone.value.price = _getZonePrice(currentZone.value.area); // TODO: move to utils?

            const valid = await _validateZoneWithLoading(currentZone.value);
            currentZoneError.value = first(currentZone.value.errorMessages);

            // when we have inter-zone constraints..
            // const valid = _allZonesValid();

            _emitResult();

            return valid;
        }

        const onFilesUploaded = async (files: File[]) => {
            await _executeWithLoading(async () => await _importContext.importFiles(files));
        }

        const _executeWithLoading = async <RESULT>(toExecute: () => Promise<RESULT>): Promise<RESULT> => {
            emit('loading', true);

            let result: RESULT;
            try {
                result = await toExecute();
            } catch (ex) {
                console.error(ex);
            }

            emit('loading', false);

            return result;
        }

        const _validateZoneWithLoading = async (cz: IDrawZone): Promise<boolean> => {
            return await _executeWithLoading(async () => await _validateZone(cz));
        }

        const _validateZone = async (cz: IDrawZone): Promise<boolean> => {
            const validationResult = await klValidateYup(cz, props.rules);
            cz.errorMessages = validationResult.errors;
            cz.isValid = validationResult.valid;

            return validationResult.valid;
        }

        const remove = async (drawZone: IDrawZone) => {
            const feature = _context.findFeature(drawZone.id);

            _context.removeFeature(feature);

            zones.value = filter(zones.value, (cz: IDrawZone) => cz.id !== drawZone.id);
            KlDrawZoneMapUtils.updateZoneNames(zones.value, props.reference, _manualZoneNames.value);

            if (currentZone.value?.id === drawZone.id) {
                _setCurrentZone(null);
            }

            _emitResult();
        }

        const removeCurrentZone = async () => {
            // can be null when called during original draw of feature
            if (currentZone.value) {
                await remove(currentZone.value);
            }

            if (props.simpleDrawMode) {
                _setState(EDrawZoneMapState.create);
            }
            else {
                _setState(EDrawZoneMapState.overview);
            }
        }

        const revertCurrentZone = async () => {
            // adding new zone?
            if (addingNewZone.value) {
                await removeCurrentZone();
                return
            }

            console.assert(!!_zonesBackup.value, 'revertCurrentZone: _zonesBackup should not be null');
            console.assert(!!_manualZoneNamesBackup.value, 'revertCurrentZone: _manualZoneNamesBackup should not be null');

            zones.value = cloneDeep(_zonesBackup.value);
            _manualZoneNames.value = cloneDeep(_manualZoneNamesBackup.value);

            _emitResult();

            _context.revertFeatureChanges();
            showList();
        }

        const _backupZones = () => {
            _zonesBackup.value = cloneDeep(zones.value);
            _manualZoneNamesBackup.value = cloneDeep(_manualZoneNames.value);
        }

        const _startAddingNewZones = () => {
            _zonesBackup.value = null;
            addingNewZone.value = true;
        }

        const _setCurrentZone = (zone: IDrawZone) => {
            if (!zone) {
                currentZone.value = null;
                currentZoneError.value = null;
                currentZoneName.value = null;
                editZoneName.value = false;
                editZoneNameHasValidationError.value = false;

                _context.setCurrentFeature(null);
            } else {
                currentZone.value = zone;
                currentZoneError.value = first(zone.errorMessages);
                currentZoneName.value = currentZone.value.name;
                editZoneName.value = false;
                editZoneNameHasValidationError.value = false;

                const feature = _context.findFeature(currentZone.value.id);
                if (feature) {
                    _context.setCurrentFeature(feature);
                }
            }
        }

        const _setState = (state: EDrawZoneMapState) => {
            // console.log('_setState', state);
            _state.value = state;
            if (state === EDrawZoneMapState.overview) {
                listZones.value = true;
                createZone.value = false;
                importZone.value = false;
                editZone.value = false;

                _zonesBackup.value = null;
                addingNewZone.value = false;
            } else if (state === EDrawZoneMapState.create) {
                listZones.value = false;
                createZone.value = true;
                importZone.value = false;
                editZone.value = false;
            } else if (state === EDrawZoneMapState.edit) {
                listZones.value = false;
                createZone.value = false;
                importZone.value = false;
                editZone.value = true;
            } else if (state === EDrawZoneMapState.import) {
                listZones.value = false;
                createZone.value = false;
                importZone.value = true;
                editZone.value = false;
            }

            emit('state', state);
        }

        const _emitResult = () => {

            const result: IDrawZoneMapGeometry[] = zones.value.map((cz: IDrawZone) => {
                const feature = _context.findFeature(cz.id);
                const geometry = feature?.getGeometry();

                const dzmGeometry: IDrawZoneMapGeometry = {
                    name: cz.name,
                    coordinates: cz.coordinates,
                    isValid: cz.isValid,
                    orgZoneInfo: cz.orgZoneInfo,
                    geometryInfo: cz.geometryInfo,

                    wkt: geometry ? new WKT().writeGeometry(geometry) : undefined,
                }

                return dzmGeometry;
            });

            emit('input', result);
            if (props.modMaprequestZone) {
                emit('zone-price', getTotalPrice());
            }
        }

        const _emitEditingResult = () => {
            emit('input', null);
        }

        const _notifyStateAsInvalidBecauseEditing = () => {
            // KAN IK DIT NIET ALTIJD DOEN, IPV enkel bij this.isConfirmVisible??
            // > let op: 'start create' > notify > created > notify > edit > notify [> edit > notify]

            // TODO? ook niet echt logisch.. dit is meer logica voor de parent component
            // reden: hier zouden we beter de state emitten (= 'edit-state'). Het is aan de parent om te beslissen wat hij daarmee doet.
            if (isConfirmVisible.value) {
                _emitEditingResult();
                if (currentZone.value) {
                    currentZone.value.isValid = undefined;
                }
            }
        }

        const _selectZone = (drawZoneId: number) => {
            const selectedFeature: Feature = _context.findFeature(drawZoneId);
            const selectedZone: IDrawZone = zones.value?.find((cz: IDrawZone) => cz.id === drawZoneId);
            _setCurrentZone(selectedZone);

            _context.selectFeature(selectedFeature);
        }

        const _reselectZone = (drawZoneId: number) => {
            const selectedFeature: Feature = _context.findFeature(drawZoneId);
            const selectedZone: IDrawZone = zones.value?.find((cz: IDrawZone) => cz.id === drawZoneId);
            _setCurrentZone(selectedZone);

            _context.reselectFeature(selectedFeature);
        }

        const _editCurrentZone = () => {
            _importContext.clearImportErrors();
            _setState(EDrawZoneMapState.edit);
            _notifyStateAsInvalidBecauseEditing();
        }

        const _edit = (drawZoneId: number) => {
            _selectZone(drawZoneId);
            _editCurrentZone();

            const selectedFeature: Feature = _context.findFeature(drawZoneId);
            emit('zoom-to-feature', selectedFeature);
        }

        const _editAfterImport = (drawZoneId: number) => {
            _startAddingNewZones();
            _edit(drawZoneId);
        }

        const edit = (drawZoneId: number) => {
            _backupZones();
            _edit(drawZoneId);
        }

        // TODO: ?? inject routing logic ??
        const editKlbZone = (drawZoneId: number) => {
            const selectedZone: IDrawZone = zones.value.find((cz: IDrawZone) => cz.id === drawZoneId);
            router.push(selectedZone.orgZoneInfo?.editLink);
        }
        const addNewKlbZone = () => {
            router.push(props.addKlbZoneLink);
        }
        const addNewKlimZone = () => {
            router.push(props.addKlimZoneLink);
        }
        // END ROUTING LOGIC

        const onKlbZoneHover = (drawZoneId: number) => {
            const selectedFeature: Feature = _context.findFeature(drawZoneId);
            _context.selectFeature(selectedFeature);
        }

        const onKlbToggleShowActive = (newValue: boolean) => {
            emit('toggle-show-active', newValue);
        }

        const initZones = async (zones: IDrawZoneMapGeometry[]) => {
            _manualZoneNames.value = {};

            if (props.forGeometry) {
                _restoreGeometryZone(first(zones));
                _setState(EDrawZoneMapState.overview);
            }
            else if (props.forKlbZones) {
                _restoreKlbZones(zones);
                _setState(EDrawZoneMapState.overview);
            }
            else {
                if (!isEmpty(zones)) {
                    // TEMP: restore van 1 enkele zone = backward compatible
                    // = momenteel wordt die restore ook enkel gebruikt voor single zones (search, opnieuw indienen maprequest)

                    // LET OP: het wordt ook wel getriggered bij een hot reload!!

                    await _restoreZone(first(zones));
                } else {
                    if (props.simpleDrawMode) {
                        _setState(EDrawZoneMapState.create);
                    }
                    else {
                        _setState(EDrawZoneMapState.overview);
                    }
                }
            }
        }

        const _createFeatureFromDrawZoneMapGeometry = (zone: IDrawZoneMapGeometry): Feature => {
            const feature = KlDrawZoneMapUtils.createFeatureFromDrawZoneMapGeometry(zone);

            if (import.meta.env.VITE_MAP_AUTO_TRANSFORM_TO_LAMBERT2008) {
                if (feature.getGeometry()) {
                    if (props.projection === 'EPSG:3812') {
                        if (!useGeometryProjectionStore().isLambert2008(feature)) {
                            console.log('MIGRATION.INIT: Auto transform of zone to EPSG:3812');
                            useGeometryProjectionStore().transformFeatureToLambert2008(feature);
                        }
                    }
                }
            }

            return feature;
        }

        const _restoreGeometryZone = (origZone: IDrawZoneMapGeometry) => {

            _clearAll();

            if (isEmpty(origZone)) {
                return;
            }

            if (!origZone.wkt) {
                throw new Error('_restoreGeometryZone expects wkt');
            }

            const feature = _createFeatureFromDrawZoneMapGeometry(origZone);

            _context.addFeature(feature); // add feature to map before awaits (visual optimisation)
            const newZone = _createDrawZone(feature); // modifies feature

            // GEOMETRY ZONE specific!!
            //newZone.name = currentZone.name;
            newZone.isValid = true;
            newZone.geometryInfo = origZone.geometryInfo;
            feature.set(EDrawZoneMapFeaturePropertyKey.geometryInfo, origZone.geometryInfo);

            // using/abusing the semi-automatic naming of zones
            // better: explicitly state these names should not be processed
            _manualZoneNames.value[newZone.id] = origZone.name;

            // _addZones will update the zone.name
            _addZones([newZone]);
            _setCurrentZone(newZone);

            // make sure the ol map is first properly rendered and filled
            nextTick(() => emit('zoom-to-feature', feature));
        }

        const _restoreKlbZones = (origZones: IDrawZoneMapGeometry[]) => {

            _clearAll();

            if (isEmpty(origZones)) {
                return;
            }

            const features: Feature[] = [];

            const newZones = origZones.map((currentZone: IDrawZoneMapGeometry): IDrawZone => {
                const feature = _createFeatureFromDrawZoneMapGeometry(currentZone);
                features.push(feature);
                _context.addFeature(feature); // add feature to map before awaits (visual optimisation)
                const newZone = _createDrawZone(feature); // modifies feature

                // ORG ZONE specific!!
                //newZone.name = currentZone.name;
                newZone.isValid = true;
                newZone.orgZoneInfo = currentZone.orgZoneInfo;
                feature.set(EDrawZoneMapFeaturePropertyKey.orgZoneInfo, currentZone.orgZoneInfo);

                // using/abusing the semi-automatic naming of zones
                // better: explicitly state these names should not be processed
                _manualZoneNames.value[newZone.id] = currentZone.name;

                return newZone;
            });

            // _addZones will update the zone.name
            _addZones(newZones);

            // make sure the ol map is first properly rendered and filled
            nextTick(() => emit('zoom-to-features', features));
        }

        const _restoreZone = async (zone: IDrawZoneMapGeometry) => {
            _clearAll();

            const feature = _createFeatureFromDrawZoneMapGeometry(zone);
            const draftZone = await _addDraft(feature);
            edit(draftZone.id);
        }

        const onRemoveGeometry = () => {
            emit('remove-geometry');
        }

        return {
            zones,
            currentZone,
            currentZoneError,

            importErrors: _importContext.importErrors,
            importWarnings: _importContext.importWarnings,
            hasImportErrors: _importContext.hasImportErrors,
            hasImportWarnings: _importContext.hasImportWarnings,
            onCancelImport: _importContext.onCancelImport,
            onContinueImport: _importContext.onContinueImport,
            showLambertImportWarning: _importContext.showLambertImportWarning,

            listZones,
            editZone,
            createZone,
            importZone,
            addingNewZone,

            editZoneName,
            currentZoneName,
            editZoneNameHasValidationError,

            mapRequestLargePrice,
            mapRequestSmallPrice,
            imklPrice,
            mapRequestSizeTreshold,
            formatCurrency,

            canEditZoneName,
            importMoreInfoLink,
            maxFilesToImport,
            currentPolygonFeatureAreaFormatted,
            currentZoneDetailInfo,
            currentZonePrice,
            zoneDetailTitle,
            zoneDetailDescription,
            applyVat,
            sidebarTitle,
            sidebarText,
            importButtonLabel,
            canAddMoreZones,
            hasZones,
            isConfirmVisible,
            isRemoveVisible,

            onCloseWarnings,
            onEditZoneName,
            onStopEditZoneName,
            onInputZoneName,
            getZoneListInfo,
            getTotalPrice,
            getTotalImklPrice,
            getImklDescription,

            onDrawInteraction_DrawEnd,
            onSelectInteraction_Select,
            onModifyInteraction_ModifyEnd,

            //showList,
            onCreate,
            onZoneImport,
            onFilesUploaded,
            onConfirm,

            remove,
            removeCurrentZone,
            revertCurrentZone,
            edit,

            editKlbZone,
            addNewKlbZone,
            addNewKlimZone,
            onKlbZoneHover,
            onKlbToggleShowActive,

            onRemoveGeometry,

            initZones,

            //dev_remove,
        }
    }
})
